import React, { useState, useEffect } from 'react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths, differenceInDays, startOfWeek, endOfWeek, addWeeks, subWeeks, startOfDay } from 'date-fns';
import { Calendar, MapPin, Briefcase, Users, CalendarX, Clock, ChevronLeft, ChevronRight } from 'lucide-react';
import { apiClient } from '../utils/api';
import { useAuth } from '../hooks/useAuth';
import { useFeature } from '../contexts/FeaturesContext';
import { useCustodyParents } from '../hooks/useCustodyParents';
import EnhancedCalendar from './EnhancedCalendar';
import { getConfiguredTimezone, getCurrentTimeInTimezone, toConfiguredTimezone } from '../utils/timezoneUtils';
import { parseLocalDate } from '../utils/dateUtils';

interface Override {
  id: string;
  override_date: string;
  override_type: 'at_parent1' | 'at_parent2' | 'at_toms' | 'at_julies' | 'work' | 'friend' | 'event' | 'other';
  reason?: string;
  location?: string;
  status: 'pending' | 'approved' | 'rejected';
  days_notice: number;
  coverage_status?: 'auto_handled' | 'covered' | 'needs_coverage' | 'no_chores';
}

interface CustodyData {
  date: string;
  // primary = with the family's first custody parent (kids_present)
  location: 'primary' | 'other';
  isTransition?: boolean;
  transitionTime?: string;
}

export function AvailabilityCalendar() {
  const { user } = useAuth();
  const custodyEnabled = useFeature('custody_schedule');
  const custodyParents = useCustodyParents();
  const primaryHouse = `${custodyParents.primary}'s House`;
  const otherHouse = `${custodyParents.other}'s House`;

  // Override types - filter custody options based on feature toggle.
  // at_toms / at_julies are legacy stored codes for at_parent1 / at_parent2:
  // kept so old overrides still get a label, but not offered as new choices.
  const overrideTypes = [
    ...(custodyEnabled ? [
      { value: 'at_parent2', label: `At ${otherHouse}`, icon: MapPin, color: 'bg-purple-500' },
      { value: 'at_parent1', label: `At ${primaryHouse}`, icon: MapPin, color: 'bg-blue-500' },
      { value: 'at_julies', label: `At ${otherHouse}`, icon: MapPin, color: 'bg-purple-500', legacy: true },
      { value: 'at_toms', label: `At ${primaryHouse}`, icon: MapPin, color: 'bg-blue-500', legacy: true },
    ] : []),
    { value: 'work', label: "Work", icon: Briefcase, color: 'bg-green-500' },
    { value: 'friend', label: "Friend's House", icon: Users, color: 'bg-blue-500' },
    { value: 'event', label: "Event/Activity", icon: CalendarX, color: 'bg-orange-500' },
    { value: 'other', label: "Other", icon: Calendar, color: 'bg-gray-500' }
  ];
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [overrides, setOverrides] = useState<Override[]>([]);
  const [custodySchedule, setCustodySchedule] = useState<CustodyData[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [calendarView, setCalendarView] = useState<'month' | 'week' | 'agenda'>('week');
  const [todayInTimezone, setTodayInTimezone] = useState<Date>(new Date());
  const [formData, setFormData] = useState({
    override_type: 'other' as any,
    reason: '',
    location: '',
    all_day: true,
    start_time: '',
    end_time: ''
  });

  useEffect(() => {
    if (user) {
      fetchOverrides();
      fetchCustodySchedule();
      updateTodayInTimezone();
    }
  }, [user, currentMonth]);

  const updateTodayInTimezone = async () => {
    try {
      const today = await getCurrentTimeInTimezone();
      setTodayInTimezone(startOfDay(today));
    } catch (error) {
      console.error('Error getting timezone:', error);
      setTodayInTimezone(startOfDay(new Date()));
    }
  };

  const fetchOverrides = async () => {
    try {
      const start = startOfMonth(currentMonth);
      const end = endOfMonth(currentMonth);
      const response = await apiClient.getAvailabilityOverrides(
        user!.id,
        format(start, 'yyyy-MM-dd'),
        format(end, 'yyyy-MM-dd')
      );
      setOverrides(response);
    } catch (error) {
      console.error('Error fetching overrides:', error);
    }
  };

  const fetchCustodySchedule = async () => {
    try {
      const start = startOfMonth(currentMonth);
      const end = endOfMonth(addMonths(currentMonth, 1)); // Get next month too
      const response = await apiClient.get(`/api/schedule/family-schedule?start_date=${format(start, 'yyyy-MM-dd')}&end_date=${format(end, 'yyyy-MM-dd')}`);

      // Check if response has the expected structure
      const data = response.data || response;

      // Handle error responses
      if (data && data.detail) {
        console.error('API error:', data.detail);
        setCustodySchedule([]);
        return;
      }

      // Handle both response formats: {daily_schedules: [...]} and {schedule: [...]}
      const scheduleData = data.daily_schedules || data.schedule || [];

      if (!Array.isArray(scheduleData)) {
        console.warn('Invalid custody schedule response structure:', data);
        setCustodySchedule([]);
        return;
      }

      // Transform the response to extract custody data for the current user
      const custody: CustodyData[] = scheduleData.map((day: any) => {
        // Check if current user is in kids_present array (Go backend format)
        const isAtParent1 = day.kids_present && Array.isArray(day.kids_present) && day.kids_present.includes(user!.username);
        // Parse date as local time, not UTC (avoid timezone shift)
        const [year, month, dayNum] = day.date.split('-').map(Number);
        const dayOfWeek = new Date(year, month - 1, dayNum).getDay();

        // Mark Friday and Sunday as transition days
        const isTransition = dayOfWeek === 5 || dayOfWeek === 0;
        const transitionTime = dayOfWeek === 5 ? 'Changeover at 6pm' : dayOfWeek === 0 ? 'Changeover at 6pm' : undefined;

        return {
          date: day.date,
          location: isAtParent1 ? 'primary' : 'other',
          isTransition,
          transitionTime
        };
      });

      setCustodySchedule(custody);
    } catch (error) {
      console.error('Error fetching custody schedule:', error);
      setCustodySchedule([]);
    }
  };

  const handleDateClick = (date: Date) => {
    // Use timezone-aware today
    const today = startOfDay(todayInTimezone);
    
    if (date < today) {
      return; // Can't modify past dates
    }
    
    setSelectedDate(date);
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedDate) return;

    try {
      const response = await apiClient.createAvailabilityOverride({
        override_date: format(selectedDate, 'yyyy-MM-dd'),
        ...formData
      });

      if (response.message || response.override_id) {
        const daysNotice = response.days_notice;
        if (response.requires_coverage || daysNotice < 3) {
          alert(`Override created! You have ${daysNotice} days notice, so your chores will be offered for bidding.`);
        } else if (response.status === 'approved') {
          alert('Override created and approved! Your chores will be automatically redistributed.');
        } else {
          alert('Override created! Waiting for parent approval.');
        }
        fetchOverrides();
        setShowModal(false);
        resetForm();
      }
    } catch (error) {
      console.error('Error creating override:', error);
      alert('Failed to create override');
    }
  };

  const resetForm = () => {
    setFormData({
      override_type: 'other',
      reason: '',
      location: '',
      all_day: true,
      start_time: '',
      end_time: ''
    });
  };

  const getOverrideStyle = (override: Override) => {
    const overrideDate = parseLocalDate(override.override_date);
    const daysNotice = differenceInDays(overrideDate, todayInTimezone);

    if (override.status === 'rejected') {
      return { color: 'bg-red-200', icon: '❌', tooltip: 'Rejected' };
    }

    if (override.status === 'pending') {
      return { color: 'bg-yellow-200', icon: '⏳', tooltip: 'Pending approval' };
    }

    if (daysNotice >= 3 || override.coverage_status === 'auto_handled') {
      return { color: 'bg-green-200', icon: '✓', tooltip: 'Chores auto-redistributed' };
    } else if (override.coverage_status === 'covered') {
      return { color: 'bg-blue-200', icon: '🏷️', tooltip: 'Chores covered' };
    } else if (override.coverage_status === 'needs_coverage') {
      return { color: 'bg-red-300', icon: '⚠️', tooltip: 'Need coverage!' };
    }

    return { color: 'bg-gray-200', icon: '•', tooltip: 'No chores affected' };
  };

  // Calculate days based on current view
  const days = calendarView === 'week' 
    ? eachDayOfInterval({
        start: startOfWeek(currentMonth),
        end: endOfWeek(currentMonth)
      })
    : eachDayOfInterval({
        // For month view, show complete weeks (including days from prev/next months)
        start: startOfWeek(startOfMonth(currentMonth)),
        end: endOfWeek(endOfMonth(currentMonth))
      });

  const isOlderKid = user?.username && ['gus', 'mo'].includes(user.username);

  // Check if custody schedule has data
  const hasCustodyData = custodySchedule.length > 0

  return (
    <div className="bg-white rounded-lg shadow p-4 sm:p-6">
      {/* Informational banner explaining the feature */}
      <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-start gap-2">
          <div className="text-blue-600 mt-0.5">
            <Calendar className="h-5 w-5" />
          </div>
          <div>
            <h3 className="font-semibold text-blue-900 mb-1">What is Availability?</h3>
            <p className="text-sm text-blue-800">
              Mark days when you'll be away or unavailable for chores. This helps coordinate chore assignments
              so your chores can be reassigned when you're not home. The more notice you give, the easier it is to redistribute chores!
            </p>
            {hasCustodyData && (
              <p className="text-xs text-blue-700 mt-2">
                💡 Your custody schedule is shown below. Colored backgrounds indicate which parent's house you're at.
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-4">
        <div>
          <h2 className="text-lg sm:text-xl font-bold">My Availability Calendar</h2>
          {hasCustodyData && (
            <div className="flex gap-4 mt-1 text-xs">
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 bg-blue-100 border border-blue-300 rounded"></span>
                {primaryHouse}
              </span>
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 bg-pink-100 border border-pink-300 rounded"></span>
                {otherHouse}
              </span>
              <span className="flex items-center gap-1">
                ⏰ Changeover at 6pm
              </span>
            </div>
          )}
        </div>
        <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4">
          <div className="flex gap-1 bg-gray-100 rounded-lg p-1 w-full sm:w-auto">
            <button
              onClick={() => setCalendarView('week')}
              className={`px-3 py-1 text-sm rounded transition-colors ${
                calendarView === 'week' 
                  ? 'bg-white text-blue-600 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Week
            </button>
            <button
              onClick={() => setCalendarView('month')}
              className={`px-3 py-1 text-sm rounded transition-colors ${
                calendarView === 'month' 
                  ? 'bg-white text-blue-600 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Month
            </button>
            <button
              onClick={() => setCalendarView('agenda')}
              className={`px-3 py-1 text-sm rounded transition-colors ${
                calendarView === 'agenda' 
                  ? 'bg-white text-blue-600 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Agenda
            </button>
          </div>
          <div className="flex gap-2 items-center w-full sm:w-auto justify-center">
            <button
              onClick={() => {
                if (calendarView === 'week') {
                  setCurrentMonth(subWeeks(currentMonth, 1));
                } else {
                  setCurrentMonth(subMonths(currentMonth, 1));
                }
              }}
              className="p-2 hover:bg-gray-100 rounded"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="px-2 sm:px-4 py-2 font-semibold min-w-[140px] text-center">
              {calendarView === 'week' 
                ? `Week of ${format(startOfWeek(currentMonth), 'MMM d')}` 
                : format(currentMonth, 'MMMM yyyy')}
            </span>
            <button
              onClick={() => {
                if (calendarView === 'week') {
                  setCurrentMonth(addWeeks(currentMonth, 1));
                } else {
                  setCurrentMonth(addMonths(currentMonth, 1));
                }
              }}
              className="p-2 hover:bg-gray-100 rounded"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Month View */}
      {calendarView === 'month' && (
        <>
          <div className="grid grid-cols-7 gap-1 mb-2">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className="text-center font-semibold text-xs sm:text-sm p-1 sm:p-2">
                <span className="hidden sm:inline">{day}</span>
                <span className="sm:hidden">{day.slice(0, 1)}</span>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-1">
            {days.map(day => {
              const dayOverrides = overrides.filter(o =>
                isSameDay(parseLocalDate(o.override_date), day)
              );
              const isPast = day < todayInTimezone;
              const isToday = isSameDay(day, todayInTimezone);

              // Find custody info for this day
              const custodyInfo = custodySchedule.find(c =>
                c.date === format(day, 'yyyy-MM-dd')
              );

              return (
                <div
                  key={day.toString()}
                  onClick={() => !isPast && handleDateClick(day)}
                  className={`
                    p-1 sm:p-2 h-16 sm:h-20 rounded cursor-pointer relative
                    ${!isSameMonth(day, currentMonth) ? 'bg-gray-50 border border-gray-200' :
                      custodyInfo?.location === 'primary' ? 'bg-blue-100 border-2 border-blue-400' : 'bg-pink-100 border-2 border-pink-400'}
                    ${isToday ? 'ring-2 ring-amber-500 ring-offset-1' : ''}
                    ${isPast ? 'opacity-50 cursor-not-allowed' : 'hover:brightness-95'}
                  `}
                >
                  <div className={`font-semibold text-xs sm:text-sm ${isToday ? 'text-amber-700' : ''}`}>
                    {format(day, 'd')}
                  </div>

                  {/* Custody indicator */}
                  {custodyInfo && (
                    <div className="absolute top-1 right-1 text-xs">
                      <span className={`${custodyInfo.location === 'primary' ? 'text-blue-600' : 'text-pink-600'}`}>
                        {custodyInfo.location === 'primary' ? '👨' : '👩'}
                      </span>
                      {custodyInfo.isTransition && (
                        <div className="absolute top-5 right-0 text-[10px] text-amber-600">
                          ⏰ 6pm
                        </div>
                      )}
                    </div>
                  )}

                  {dayOverrides.map(override => {
                    const style = getOverrideStyle(override);
                    const typeInfo = overrideTypes.find(t => t.value === override.override_type);

                    return (
                      <div
                        key={override.id}
                        className={`text-xs mt-1 p-1 rounded ${style.color}`}
                        title={`${style.tooltip} - ${override.reason || typeInfo?.label}`}
                      >
                        <span>{style.icon} {typeInfo?.label.substring(0, 8)}</span>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Week View */}
      {calendarView === 'week' && (
        <div className="space-y-2">
          <div className="grid grid-cols-7 gap-2">
            {days.slice(0, 7).map(day => {
              const dayOverrides = overrides.filter(o =>
                isSameDay(parseLocalDate(o.override_date), day)
              );
              const isPast = day < todayInTimezone;

              // Find custody info for this day
              const custodyInfo = custodySchedule.find(c =>
                c.date === format(day, 'yyyy-MM-dd')
              );

              return (
                <div
                  key={day.toString()}
                  onClick={() => !isPast && handleDateClick(day)}
                  className={`
                    p-3 border rounded cursor-pointer relative
                    ${isPast ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-100'}
                    ${custodyInfo?.location === 'primary' ? 'bg-blue-50 border-blue-300' : 'bg-pink-50 border-pink-300'}
                  `}
                >
                  <div className="font-semibold text-sm text-center">
                    {format(day, 'EEE')}
                  </div>
                  <div className="text-lg text-center mb-2">
                    {format(day, 'd')}
                  </div>

                  {/* Custody indicator */}
                  {custodyInfo && (
                    <div className="absolute top-2 right-2 text-sm">
                      <span className={`${custodyInfo.location === 'primary' ? 'text-blue-600' : 'text-pink-600'}`}>
                        {custodyInfo.location === 'primary' ? '👨' : '👩'}
                      </span>
                      {custodyInfo.isTransition && (
                        <div className="text-[10px] text-amber-600 mt-1">
                          ⏰ 6pm
                        </div>
                      )}
                    </div>
                  )}

                  {dayOverrides.map(override => {
                    const style = getOverrideStyle(override);
                    const typeInfo = overrideTypes.find(t => t.value === override.override_type);

                    return (
                      <div
                        key={override.id}
                        className={`text-xs mt-1 p-1 rounded ${style.color}`}
                        title={`${style.tooltip} - ${override.reason || typeInfo?.label}`}
                      >
                        <span>{style.icon} {typeInfo?.label}</span>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Agenda View */}
      {calendarView === 'agenda' && (
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {overrides.length === 0 ? (
            <div className="text-gray-500 text-center py-8">
              No upcoming unavailable days
            </div>
          ) : (
            overrides
              .filter(o => parseLocalDate(o.override_date) >= todayInTimezone)
              .sort((a, b) => parseLocalDate(a.override_date).getTime() - parseLocalDate(b.override_date).getTime())
              .map(override => {
                const style = getOverrideStyle(override);
                const typeInfo = overrideTypes.find(t => t.value === override.override_type);
                const date = parseLocalDate(override.override_date);

                return (
                  <div
                    key={override.id}
                    className={`p-3 border rounded ${style.color}`}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-semibold">
                          {format(date, 'EEEE, MMMM d, yyyy')}
                        </div>
                        <div className="text-sm mt-1">
                          {style.icon} {typeInfo?.label}
                        </div>
                        {override.reason && (
                          <div className="text-sm mt-1 italic">
                            {override.reason}
                          </div>
                        )}
                      </div>
                      <div className="text-sm">
                        {style.tooltip}
                      </div>
                    </div>
                  </div>
                );
              })
          )}
        </div>
      )}

      <div className="mt-4 text-sm">
        <div className="flex gap-4 flex-wrap">
          <span className="flex items-center gap-1">
            <div className="w-4 h-4 bg-green-200 rounded"></div> Auto-handled (3+ days)
          </span>
          <span className="flex items-center gap-1">
            <div className="w-4 h-4 bg-blue-200 rounded"></div> Covered
          </span>
          <span className="flex items-center gap-1">
            <div className="w-4 h-4 bg-red-300 rounded"></div> Needs coverage
          </span>
          <span className="flex items-center gap-1">
            <div className="w-4 h-4 bg-yellow-200 rounded"></div> Pending
          </span>
        </div>
      </div>

      {showModal && selectedDate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-bold mb-4">
              Mark Unavailable - {format(selectedDate, 'MMMM d, yyyy')}
            </h3>
            
            <div className="mb-2 text-sm">
              Days notice: <strong>{differenceInDays(selectedDate, todayInTimezone)}</strong>
              {differenceInDays(selectedDate, todayInTimezone) >= 3 ? (
                <span className="text-green-600"> (Auto-handled)</span>
              ) : (
                <span className="text-orange-600"> (Manual coverage needed)</span>
              )}
            </div>

            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Type</label>
                <select
                  value={formData.override_type}
                  onChange={(e) => setFormData({...formData, override_type: e.target.value as any})}
                  className="w-full p-2 border rounded"
                  required
                >
                  {overrideTypes.filter(type => !('legacy' in type)).map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Reason</label>
                <input
                  type="text"
                  value={formData.reason}
                  onChange={(e) => setFormData({...formData, reason: e.target.value})}
                  className="w-full p-2 border rounded"
                  placeholder={formData.override_type === 'work' ? "Chick-fil-A shift" : "Optional details"}
                />
              </div>

              {!formData.all_day && (
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Start Time</label>
                    <input
                      type="time"
                      value={formData.start_time}
                      onChange={(e) => setFormData({...formData, start_time: e.target.value})}
                      className="w-full p-2 border rounded"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">End Time</label>
                    <input
                      type="time"
                      value={formData.end_time}
                      onChange={(e) => setFormData({...formData, end_time: e.target.value})}
                      className="w-full p-2 border rounded"
                    />
                  </div>
                </div>
              )}

              <div className="mb-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.all_day}
                    onChange={(e) => setFormData({...formData, all_day: e.target.checked})}
                    className="rounded"
                  />
                  <span className="text-sm">All day</span>
                </label>
              </div>

              {isOlderKid && formData.override_type === 'work' && (
                <div className="mb-4 p-3 bg-green-100 rounded text-sm">
                  <Clock className="inline w-4 h-4 mr-1" />
                  Work schedules are auto-approved for you!
                </div>
              )}

              <div className="flex gap-2">
                <button
                  type="submit"
                  className="flex-1 bg-blue-500 text-white py-2 rounded hover:bg-blue-600"
                >
                  Submit
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="flex-1 bg-gray-300 text-gray-700 py-2 rounded hover:bg-gray-400"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}